import React from "react";
import { Modal, Box, Typography, Button, IconButton } from "@mui/material";

const DonationStatusModal = ({
  open,
  type = "info", // "success" | "error" | "warning" | "info"
  title,
  message,
  details = null,
  primaryButtonText,
  onPrimaryAction,
  onClose,
}) => {
  const isMobile = window.innerWidth <= 600;

  const isSuccess = type === "success";
  const isError = type === "error";

  const defaultTitle = isSuccess
    ? "Thank You For Your Support!"
    : isError
    ? "Unable to Complete Request"
    : "Notice";

  const defaultButtonText = isSuccess
    ? "Continue"
    : isError
    ? "Try Again"
    : "Close";

  const handlePrimaryClick = () => {
    if (onPrimaryAction) {
      onPrimaryAction();
    } else if (onClose) {
      onClose();
    }
  };

  return (
    <Modal
      open={Boolean(open)}
      onClose={onClose}
      aria-labelledby="donation-status-modal-title"
      aria-describedby="donation-status-modal-description"
      closeAfterTransition
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backdropFilter: "blur(5px)",
        backgroundColor: "rgba(0, 20, 40, 0.55)",
        p: 2,
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: isMobile ? "92vw" : "480px",
          bgcolor: "#FFFFFF",
          borderRadius: "20px",
          boxShadow: "0 20px 40px -15px rgba(0, 52, 91, 0.35)",
          p: isMobile ? 3 : 4,
          textAlign: "center",
          outline: "none",
          border: isSuccess
            ? "2px solid #A7F3D0"
            : isError
            ? "2px solid #FECDD3"
            : "2px solid #E2E8F0",
          animation: "modalPopIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
          "@keyframes modalPopIn": {
            from: {
              opacity: 0,
              transform: "scale(0.92) translateY(12px)",
            },
            to: {
              opacity: 1,
              transform: "scale(1) translateY(0)",
            },
          },
        }}
      >
        {/* Top Close Button */}
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            color: "#94A3B8",
            "&:hover": {
              color: "#334155",
              backgroundColor: "#F1F5F9",
            },
          }}
          aria-label="close"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </IconButton>

        {/* Status Icon Badge */}
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: "50%",
            mx: "auto",
            mb: 2.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: isSuccess
              ? "linear-gradient(135deg, #10B981 0%, #059669 100%)"
              : isError
              ? "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)"
              : "linear-gradient(135deg, #09AACC 0%, #00345B 100%)",
            boxShadow: isSuccess
              ? "0 10px 20px -5px rgba(16, 185, 129, 0.45)"
              : isError
              ? "0 10px 20px -5px rgba(239, 68, 68, 0.45)"
              : "0 10px 20px -5px rgba(9, 170, 204, 0.45)",
          }}
        >
          {isSuccess ? (
            <svg
              width="36"
              height="36"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#FFFFFF"
              strokeWidth="2.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          ) : isError ? (
            <svg
              width="36"
              height="36"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#FFFFFF"
              strokeWidth="2.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          ) : (
            <svg
              width="36"
              height="36"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#FFFFFF"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
          )}
        </Box>

        {/* Title */}
        <Typography
          id="donation-status-modal-title"
          variant="h5"
          component="h2"
          sx={{
            fontFamily: "'Montserrat', sans-serif",
            fontWeight: 700,
            fontSize: isMobile ? "20px" : "22px",
            color: "#00345B",
            mb: 1.5,
            lineHeight: 1.3,
          }}
        >
          {title || defaultTitle}
        </Typography>

        {/* Message */}
        {message && (
          <Typography
            id="donation-status-modal-description"
            sx={{
              fontFamily: "'Source Sans Pro', sans-serif",
              fontSize: isMobile ? "14px" : "15.5px",
              color: "#475569",
              lineHeight: 1.55,
              mb: details ? 2.5 : 3.5,
            }}
          >
            {message}
          </Typography>
        )}

        {/* Optional Structured Details Card */}
        {details && (
          <Box
            sx={{
              backgroundColor: isSuccess ? "#F0FDF4" : "#F8FAFC",
              border: isSuccess ? "1px solid #BBF7D0" : "1px solid #E2E8F0",
              borderRadius: "12px",
              p: 2,
              mb: 3,
              textAlign: "left",
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            {Object.entries(details).map(([key, val]) => {
              if (!val) return null;
              return (
                <Box
                  key={key}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    fontSize: "13.5px",
                  }}
                >
                  <Typography
                    component="span"
                    sx={{ color: "#64748B", fontWeight: 500, textTransform: "capitalize" }}
                  >
                    {key.replace(/([A-Z])/g, " $1")}:
                  </Typography>
                  <Typography component="span" sx={{ color: "#00345B", fontWeight: 700 }}>
                    {String(val)}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}

        {/* Action Button */}
        <Button
          onClick={handlePrimaryClick}
          variant="contained"
          fullWidth
          sx={{
            fontFamily: "'Montserrat', sans-serif",
            fontWeight: 700,
            fontSize: "15px",
            textTransform: "none",
            borderRadius: "12px",
            py: 1.4,
            backgroundColor: isSuccess ? "#00345B" : isError ? "#DC2626" : "#09AACC",
            boxShadow: isSuccess
              ? "0 4px 14px rgba(0, 52, 91, 0.3)"
              : isError
              ? "0 4px 14px rgba(220, 38, 38, 0.3)"
              : "0 4px 14px rgba(9, 170, 204, 0.3)",
            "&:hover": {
              backgroundColor: isSuccess ? "#00223D" : isError ? "#B91C1C" : "#078FA9",
              transform: "translateY(-1px)",
            },
            transition: "all 0.2s ease",
          }}
        >
          {primaryButtonText || defaultButtonText}
        </Button>
      </Box>
    </Modal>
  );
};

export default DonationStatusModal;
